import { CodeHighlight } from "@mantine/code-highlight";
import { Box } from "@mantine/core";
import hljs from "highlight.js/lib/core";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import classes from "./ChatMarkdown.module.css";
import JsonFormatter from "./utils/JsonFormatter";

const LANGUAGE_ALIAS_MAP: Record<string, string> = {
    ts: "typescript",
    tsx: "typescript",
    js: "javascript",
    jsx: "javascript",
    sh: "bash",
    shell: "bash",
    zsh: "bash",
    py: "python",
    rs: "rust",
    gql: "graphql",
    htm: "html",
    text: "plaintext",
    txt: "plaintext",
    json5: "json",
};

interface IProps {
    content: string;
}

function ChatMarkdown({ content }: IProps) {
    const isJson = (() => {
        try {
            JSON.parse(content);
            return content.trim().startsWith("{") || content.trim().startsWith("[");
        } catch (_e) {
            return false;
        }
    })();

    if (isJson) {
        return <JsonFormatter content={content} />;
    }

    return (
        <Box className={classes.markdown}>
            <Markdown
                remarkPlugins={[remarkGfm]}
                components={{
                    code(props) {
                        const { children, className, ...rest } = props;
                        const match = /language-(\w+)/.exec(className || "");
                        if (match) {
                            const lang = match[1].toLowerCase();
                            const canonical = LANGUAGE_ALIAS_MAP[lang] ?? lang;
                            const isLoaded = !!hljs.getLanguage(canonical);
                            if (!isLoaded) {
                                console.log(
                                    `[ChatMarkdown] Unsupported highlight.js language: "${canonical}". Falling back to plaintext.`,
                                );
                            }
                            const safeLang = isLoaded ? canonical : "plaintext";
                            return <CodeHighlight code={String(children).replace(/\n$/, "")} language={safeLang} />;
                        }
                        return (
                            <code {...rest} className={className}>
                                {children}
                            </code>
                        );
                    },
                }}
            >
                {content}
            </Markdown>
        </Box>
    );
}

export default ChatMarkdown;
